import { useCallback, useEffect, useRef, useState } from 'react'

import { htmlToInlineHtml, markersToHtml } from './converters'
import { RichTextEditor } from './index'

interface Props {
  /** Label shown above the editor. */
  title?: string
  /** Stored value. May be legacy plain text / marker text, or HTML. */
  value: string
  onChange: (value: string) => void
  placeholders?: string[]
  error?: string
  required?: boolean
  disabled?: boolean
  minHeight?: number
  className?: string
  /**
   * 'block' writes paragraph HTML — use it where the value lands inside a
   * `<div>` or other block container.
   * 'inline' flattens the result to inline HTML + `<br/>` — use it where the
   * value is interpolated into a heading or span by `utils/emailHtml.ts`.
   */
  variant?: 'block' | 'inline'
  additionalElements?: JSX.Element | null
}

/**
 * `RichTextEditor` wrapped in the label / required / error chrome the rest of
 * the form inputs use, plus the value conversions:
 *
 *  - on the way IN, a legacy plain-text body is turned into HTML so its line
 *    breaks survive (`markersToHtml` passes real HTML through untouched);
 *  - on the way OUT, an 'inline' field is flattened so it stays valid inside
 *    the heading tags the email element renderers wrap it in.
 *
 * The incoming conversion never calls `onChange`, so opening a screen and
 * saving it without touching the body leaves the stored value byte-identical.
 */
export const RichTextField = ({
  title = '',
  value,
  onChange,
  placeholders = [],
  error = '',
  required = false,
  disabled = false,
  minHeight = 200,
  className = '',
  variant = 'block',
  additionalElements = null,
}: Props) => {
  // The editor is driven by its own HTML, not by the stored value. For an
  // 'inline' field the two differ (the stored value is flattened), and feeding
  // the flattened form back in would make the editor rebuild its document on
  // every keystroke and throw the caret to the end.
  const [editorHtml, setEditorHtml] = useState(() => markersToHtml(value || ''))
  // The stored value the editor is already showing. Updated both when we push
  // an edit out and when we pull an external change in, so a re-render caused
  // by a sibling field never rebuilds this editor's document.
  const syncedValueRef = useRef<string>(value)

  useEffect(() => {
    if (value === syncedValueRef.current) return
    syncedValueRef.current = value
    setEditorHtml(markersToHtml(value || ''))
  }, [value])

  const handleChange = useCallback(
    (html: string) => {
      const next = variant === 'inline' ? htmlToInlineHtml(html) : html
      syncedValueRef.current = next
      setEditorHtml(html)
      onChange(next)
    },
    [onChange, variant],
  )

  return (
    <div className="input-container">
      {title && (
        <label className="flex gap-1 items-center text-[12px] text-gray-700 mb-1">
          <span className="truncate" title={title}>
            {title}
          </span>
          {required && <span>*</span>}
          {additionalElements}
        </label>
      )}
      <RichTextEditor
        value={editorHtml}
        onChange={handleChange}
        placeholders={placeholders}
        error={error}
        minHeight={minHeight}
        className={className}
        ariaLabel={title || undefined}
        variant={variant}
        editable={!disabled}
      />
    </div>
  )
}

export default RichTextField
